import React, { forwardRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    CircularProgress,
    Box,
    Avatar,
    Divider,
    Chip,
    useTheme,
    Slide
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});


export default function ConfirmDialog({
                                          open,
                                          title = "Confirmation",
                                          description = "",
                                          confirmLabel = "Confirmer",
                                          cancelLabel = "Annuler",
                                          onConfirm,
                                          onCancel,
                                          loading = false,
                                          color = "error",
                                          icon: Icon = WarningAmberRoundedIcon,
                                          hint,
                                          chips = [],
                                      }) {
    const theme = useTheme();

    const handleKeyDown = (e) => {
        if (loading) return;
        if (e.key === "Escape") onCancel?.();
        if (e.key === "Enter") onConfirm?.();
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={loading ? undefined : onCancel}
            onKeyDown={handleKeyDown}
            fullWidth
            maxWidth="xs"
            aria-labelledby="confirm-title"
            BackdropProps={{
                sx: {
                    backdropFilter: "blur(3px)",
                    backgroundColor: "rgba(15, 18, 23, .38)",
                },
            }}
            slotProps={{ paper: {
                elevation: 0,
                sx: {
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 18px 60px rgba(0,0,0,.25)",
                    border: `1px solid ${theme.palette.mode === "light"
                        ? "rgba(0,0,0,.06)"
                        : "rgba(255,255,255,.08)"}`,
                },
            } }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.25,
                    color: "#fff",
                    background: `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
                    position: "relative",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        background:
                            "radial-gradient(900px 220px at -10% 0%, rgba(255,255,255,.18), transparent)," +
                            "radial-gradient(580px 180px at 110% -20%, rgba(255,255,255,.12), transparent)",
                    }}
                />
                <Stack direction="row" alignItems="center" spacing={1.5} position="relative">
                    <Avatar
                        variant="rounded"
                        sx={{
                            bgcolor: "rgba(255,255,255,.2)",
                            color: "#fff",
                            width: 40,
                            height: 40,
                            backdropFilter: "blur(2px)",
                        }}
                    >
                        <Icon />
                    </Avatar>
                    <Typography id="confirm-title" variant="h6" fontWeight={900} letterSpacing={0.2}>
                        {title}
                    </Typography>
                </Stack>
            </Box>

            <DialogContent dividers sx={{ px: 3, py: 2.25 }}>
                <Stack spacing={1.25}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {description}
                    </Typography>

                    {hint ? (
                        <Typography variant="caption" color="text.secondary">
                            {hint}
                        </Typography>
                    ) : null}

                    {chips?.length ? (
                        <>
                            <Divider sx={{ my: 0.5 }} />
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {chips.map((c, i) => (
                                    <Chip
                                        key={`${c}-${i}`}
                                        size="small"
                                        label={c}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 999,
                                            fontWeight: 600,
                                            borderColor:
                                                theme.palette.mode === "light"
                                                    ? "rgba(0,0,0,.12)"
                                                    : "rgba(255,255,255,.16)",
                                        }}
                                    />
                                ))}
                            </Stack>
                        </>
                    ) : null}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onCancel}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {cancelLabel}
                </Button>
                <Button
                    variant="contained"
                    color={color}
                    onClick={onConfirm}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 800,
                        px: 2.5,
                        boxShadow: "0 8px 22px rgba(0,0,0,.18)",
                    }}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
